import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import { useLang } from "@/context/LanguageContext";
import {
  Camera, ChevronLeft, ChevronRight, Maximize2, Minimize2,
  RotateCcw, Info, MapPin, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOUR_HERO = "https://static.prod-images.emergentagent.com/jobs/236e7b77-d75d-43f0-8b66-78eefb5d55fb/images/5c9463f119ae13d7b4d09db072762e02f88043ce0daf538166197609012e3f1c.jpeg";

export default function VirtualTour() {
  const { t } = useLang();
  const [scenes, setScenes] = useState([]);
  const [activeScene, setActiveScene] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const viewerRef = useRef(null);
  const pannellumRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/virtual-tour/scenes`).then((res) => {
      setScenes(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const initViewer = useCallback(() => {
    if (!scenes.length || !viewerRef.current || !window.pannellum) return;
    if (pannellumRef.current) {
      pannellumRef.current.destroy();
      pannellumRef.current = null;
    }
    const scene = scenes[activeScene];
    pannellumRef.current = window.pannellum.viewer(viewerRef.current, {
      type: "equirectangular",
      panorama: scene.image_url,
      autoLoad: true,
      autoRotate: -2,
      compass: true,
      showControls: false,
      hfov: 110,
      minHfov: 50,
      maxHfov: 120,
      friction: 0.15,
      mouseZoom: true,
      keyboardZoom: true,
      showFullscreenCtrl: false,
    });
  }, [scenes, activeScene]);

  useEffect(() => {
    const timer = setTimeout(initViewer, 100);
    return () => {
      clearTimeout(timer);
      if (pannellumRef.current) {
        pannellumRef.current.destroy();
        pannellumRef.current = null;
      }
    };
  }, [initViewer]);

  const goToScene = (idx) => {
    if (idx >= 0 && idx < scenes.length) setActiveScene(idx);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!fullscreen) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setFullscreen(!fullscreen);
  };

  const resetView = () => {
    if (pannellumRef.current) {
      pannellumRef.current.setYaw(0);
      pannellumRef.current.setPitch(0);
      pannellumRef.current.setHfov(110);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0F172A] min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0F172A] min-h-screen" data-testid="virtual-tour-page">
      <PageSEO page="virtual-tour" />
      <Navbar />

      {/* Header */}
      <PageHero
        image={TOUR_HERO}
        tag="Immersive Experience"
        title="360° Virtual Tour"
        subtitle="Explore IIE Sigaddi Industrial Park from the comfort of your home. Click and drag to look around, scroll to zoom."
        breadcrumb={{ home: t("nav.home"), current: "360° Virtual Tour" }}
      />

      {/* Main Tour Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {scenes.length === 0 ? (
          <div className="bg-[#1E293B] border border-white/10 p-12 text-center" data-testid="no-scenes">
            <Camera className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Virtual Tour Coming Soon
            </h3>
            <p className="text-slate-400 text-sm">
              360° panoramic views of the industrial park are being prepared. Check back soon!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Viewer Container */}
            <div
              ref={containerRef}
              className="relative bg-black overflow-hidden"
              style={{ height: fullscreen ? "100vh" : "65vh", minHeight: "400px" }}
              data-testid="tour-viewer-container"
            >
              <div ref={viewerRef} className="w-full h-full" data-testid="pannellum-viewer" />

              {/* Overlay Controls */}
              <div className="absolute top-4 right-4 flex gap-2 z-10">
                <button
                  onClick={resetView}
                  className="w-9 h-9 bg-black/60 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                  title="Reset View"
                  data-testid="tour-reset-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className={`w-9 h-9 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-colors ${showInfo ? "bg-orange-500/60 text-white" : "bg-black/60 text-white hover:bg-black/80"}`}
                  title="Toggle Info"
                  data-testid="tour-info-btn"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="w-9 h-9 bg-black/60 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                  title="Fullscreen"
                  data-testid="tour-fullscreen-btn"
                >
                  {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              </div>

              {/* Scene Navigation Arrows */}
              {scenes.length > 1 && (
                <>
                  <button
                    onClick={() => goToScene(activeScene - 1)}
                    disabled={activeScene === 0}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-black/80 transition-colors disabled:opacity-30 z-10"
                    data-testid="tour-prev-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => goToScene(activeScene + 1)}
                    disabled={activeScene === scenes.length - 1}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-black/80 transition-colors disabled:opacity-30 z-10"
                    data-testid="tour-next-btn"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Scene Info Overlay */}
              <AnimatePresence>
                {showInfo && scenes[activeScene] && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="absolute bottom-4 left-4 right-4 z-10"
                  >
                    <div className="bg-black/70 backdrop-blur-md border border-white/10 p-4 max-w-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-orange-500" />
                        <span className="text-orange-400 text-xs font-bold uppercase tracking-wider"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          Scene {activeScene + 1} of {scenes.length}
                        </span>
                      </div>
                      <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase" }}>
                        {scenes[activeScene].title}
                      </h3>
                      {scenes[activeScene].description && (
                        <p className="text-slate-300 text-sm mt-1">{scenes[activeScene].description}</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Scene Counter */}
              <div className="absolute top-4 left-4 z-10 bg-black/60 backdrop-blur-sm border border-white/20 px-3 py-1.5">
                <span className="text-white text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {activeScene + 1} / {scenes.length}
                </span>
              </div>
            </div>

            {/* Scene Thumbnails */}
            {scenes.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2" data-testid="tour-thumbnails">
                {scenes.map((scene, idx) => (
                  <button
                    key={scene.id || idx}
                    onClick={() => goToScene(idx)}
                    data-testid={`tour-thumb-${idx}`}
                    className={`flex-shrink-0 relative overflow-hidden transition-all ${
                      idx === activeScene
                        ? "ring-2 ring-orange-500 scale-105"
                        : "ring-1 ring-white/10 hover:ring-orange-500/50 opacity-70 hover:opacity-100"
                    }`}
                    style={{ width: "140px", height: "80px" }}
                  >
                    <img
                      src={scene.image_url}
                      alt={scene.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 140 80'%3E%3Crect fill='%231E293B' width='140' height='80'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%2364748B' font-size='10'%3E360°%3C/text%3E%3C/svg%3E"; }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                      <span className="text-white text-xs font-bold truncate block">{scene.title}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Instructions */}
            <div className="bg-[#1E293B] border border-white/10 p-4 flex flex-wrap gap-6">
              {[
                { icon: "click + drag", label: "Look Around" },
                { icon: "scroll", label: "Zoom In/Out" },
                { icon: "arrows", label: "Next/Prev Scene" },
              ].map((tip) => (
                <div key={tip.label} className="flex items-center gap-2">
                  <span className="text-orange-400 text-xs font-bold uppercase" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {tip.icon}
                  </span>
                  <span className="text-slate-400 text-sm">{tip.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-orange-500 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-white text-2xl font-bold uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Ready to Visit in Person?
          </h2>
          <p className="text-white/80 text-sm mb-4">Schedule a guided site visit to IIE Sigaddi Industrial Park</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="/booking"
              className="bg-white text-orange-600 px-6 py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-orange-50 transition-colors"
              data-testid="tour-book-visit-btn"
            >
              Book Site Visit
            </a>
            <a
              href="tel:+919258959058"
              className="border-2 border-white text-white px-6 py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-white/10 transition-colors"
            >
              Call +91-9258959058
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
